import { http } from "@/lib/http"
import { token } from "@/lib/tokenStore"
import { endSession, currentAccessToken, resumeSession } from "@/lib/session"
import { API_BASE_URL } from "@/lib/apiConfig"
import { NATIVE_PLATFORM, PLATFORM_HEADER, REFRESH_TOKEN_HEADER, isNativeApp } from "@/lib/platform"
import type {
  AuthResponse,
  LoginPayload,
  RegisterPayload,
  PasswordRecoveryPayload,
  ResetPasswordPayload,
  VerifyEmailPayload,
  UserDto,
  MeResponse,
  UpdateProfilePayload,
  ChangePasswordPayload,
  AvatarUploadResponse,
} from "@/types/auth"

function buildApiUrl(path: string): string {
  return `${API_BASE_URL}${path.startsWith("/") ? path : `/${path}`}`
}

/**
 * Guarda o resultado de uma autenticação.
 *
 * No app nativo o refresh token vem no corpo e precisa ser persistido; no browser ele
 * chega como cookie HttpOnly e `refreshToken` vem nulo, então não há o que salvar.
 */
async function storeSession(data: AuthResponse): Promise<AuthResponse> {
  token.set(data.accessToken, data.expiresIn)
  await token.saveRefreshToken(data.refreshToken)
  return data
}

/**
 * Cabeçalho que pede o transporte por corpo. Enviado apenas no login/registro: é ali
 * que o backend decide, e grava, como aquela sessão vai transportar o refresh token.
 */
function platformHeaders(): Record<string, string> {
  return isNativeApp() ? { [PLATFORM_HEADER]: NATIVE_PLATFORM } : {}
}

/**
 * Retoma a sessão de 30 dias usando o refresh token guardado (cookie no browser,
 * Preferences no app). Reexportado aqui para que o store fale só com o service.
 */
export { resumeSession }

// ─── Microsoft SSO ──────────────────────────────────────────────────────────

/** POST /api/auth/register */
export async function register(payload: RegisterPayload): Promise<AuthResponse> {
  const data = await http.post<AuthResponse>("/api/auth/register", payload, {
    public: true,
    headers: platformHeaders(),
  })
  return storeSession(data)
}

/** POST /api/auth/login */
export async function login(payload: LoginPayload): Promise<AuthResponse> {
  const data = await http.post<AuthResponse>("/api/auth/login", payload, {
    public: true,
    headers: platformHeaders(),
  })
  return storeSession(data)
}

/** GET /api/auth/me — requer token */
export async function getMe(): Promise<UserDto> {
  return http.get<UserDto>("/api/auth/me")
}

/** GET /api/auth/me — retorna perfil completo incluindo govId */
export async function getMeProfile(): Promise<MeResponse> {
  return http.get<MeResponse>("/api/auth/me")
}

/**
 * PUT /api/auth/profile — atualiza nome, GovId e telefone do usuário autenticado.
 *
 * O backend (`UpdateProfileCommand`) espera `firstName`/`lastName` separados,
 * mais `phoneNumber`/`govId`. O formulário trabalha com um único "nome completo",
 * então quebramos no primeiro espaço. `firstName` é obrigatório no backend —
 * mandar o payload cru fazia todo salvamento (nome, tema e foto) retornar 400.
 */
export async function updateProfile(payload: UpdateProfilePayload): Promise<UserDto> {
  const fullName = payload.fullName.trim()
  const gap = fullName.indexOf(" ")

  return http.put<UserDto>("/api/auth/profile", {
    firstName: gap === -1 ? fullName : fullName.slice(0, gap),
    lastName: gap === -1 ? "" : fullName.slice(gap + 1).trim(),
    phoneNumber: payload.phone?.trim() ? payload.phone.trim() : null,
    govId: payload.govId?.trim() ? payload.govId.trim() : null,
  })
}

/** PUT /api/auth/password — altera a senha */
export async function changePassword(payload: ChangePasswordPayload): Promise<void> {
  await http.put("/api/auth/password", payload)
}

/** POST /api/auth/avatar — upload temporário da foto de perfil (multipart/form-data) */
export async function uploadAvatar(file: File): Promise<AvatarUploadResponse> {
  const t = await currentAccessToken()
  const formData = new FormData()
  // Blobs recortados chegam sem `.name` nativo no FormData ("blob"), e o backend
  // rejeita arquivo sem extensão — nome explícito derivado do MIME.
  const ext = file.type === "image/png" ? "png" : file.type === "image/webp" ? "webp" : "jpg"
  const named = (file as File).name
  const fileName = named && /\.[A-Za-z0-9]+$/.test(named) ? named : `avatar-${Date.now()}.${ext}`
  formData.append("file", file, fileName)

  const res = await fetch(buildApiUrl("/api/auth/avatar"), {
    method: "POST",
    // Fora do http.ts porque é multipart: deixar o Content-Type ser definido pelo
    // browser é o que garante o boundary correto.
    headers: t ? { Authorization: `Bearer ${t}` } : {},
    body: formData,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: "Erro ao fazer upload." }))
    throw new Error(body?.message ?? "Erro ao fazer upload.")
  }

  const json = await res.json() as { data: AvatarUploadResponse }
  return json.data
}

/** POST /api/auth/request-password-recovery */
export async function requestPasswordRecovery(payload: PasswordRecoveryPayload): Promise<void> {
  await http.post("/api/auth/request-password-recovery", payload, { public: true })
}

/** POST /api/auth/reset-password */
export async function resetPassword(payload: ResetPasswordPayload): Promise<void> {
  await http.post("/api/auth/reset-password", payload, { public: true })
}

/** POST /api/auth/verify-email */
export async function verifyEmail(payload: VerifyEmailPayload): Promise<void> {
  await http.post("/api/auth/verify-email", payload)
}

/**
 * POST /api/auth/resend-verification
 *
 * `email` é obrigatório: o backend identifica o usuário por ele (`ResendVerificationEmailCommand`),
 * não pela sessão. `public: true` porque este fluxo também é acionado a partir do login bloqueado
 * por e-mail não verificado, quando ainda não existe access token nenhum.
 */
export async function resendVerificationCode(email: string): Promise<void> {
  await http.post("/api/auth/resend-verification", { email }, { public: true })
}

/**
 * GET /api/auth/sso-session — consome o cookie HttpOnly escrito pelo callback
 * do backend e retorna um AuthResponse completo (token + user via /me).
 */
export async function ssoSession(): Promise<AuthResponse> {
  let res: Response
  try {
    res = await fetch(buildApiUrl("/api/auth/sso-session"), { credentials: "include" })
  } catch (err) {
    console.error("[ssoSession] network error:", err)
    throw new Error("Nao foi possivel conectar ao servidor de autenticacao.")
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "")
    console.error(`[ssoSession] ${res.status} ${res.statusText}`, body)
    throw new Error("Sessao SSO nao encontrada ou expirada.")
  }

  const { accessToken } = (await res.json()) as { accessToken: string }
  token.set(accessToken)
  const user = await getMe()
  return { accessToken, expiresIn: 0, user }
}

/**
 * O SSO depende de redirecionamento do browser e de cookies entre a web e a API, o que o
 * WebView do app nativo não garante. `NEXT_PUBLIC_SSO_ENABLED=false` desliga por ambiente.
 *
 * Só isso decide no cliente. Se o provedor está habilitado e configurado quem diz é a API
 * (`/api/auth/providers` + a validação de startup de `Microsoft:*`); espelhar aqui o client id,
 * o tenant e o redirect URI com variáveis `NEXT_PUBLIC_*` criava uma segunda fonte de verdade
 * que, quando divergia do pipeline, desligava o botão sem nenhum erro visível.
 */
export function isSsoAvailable(): boolean {
  if (isNativeApp()) return false
  if (process.env.NEXT_PUBLIC_SSO_ENABLED === "false") return false
  return true
}

/** Identidade Microsoft ainda sem conta, guardada pelo backend num cookie assinado. */
export type SsoPending = { email: string; fullName: string }

/** GET /api/auth/sso-pending — dados da conta Microsoft que tentou entrar sem cadastro. */
export async function getSsoPending(): Promise<SsoPending> {
  return http.get<SsoPending>("/api/auth/sso-pending", { public: true, skipAuthRefresh: true, retries: 0 })
}

/**
 * POST /api/auth/microsoft/register — cria a conta com a identidade pendente e inicia a
 * sessão. Só o nome é enviado: e-mail e identidade vêm do cookie assinado no servidor.
 */
export async function registerWithSso(fullName: string): Promise<AuthResponse> {
  const data = await http.post<AuthResponse>(
    "/api/auth/microsoft/register",
    { fullName },
    { public: true, skipAuthRefresh: true },
  )
  return storeSession(data)
}

/**
 * Redireciona o browser para o endpoint do backend que inicia o fluxo OAuth2
 * com a Microsoft. O backend gera state + PKCE, define os cookies necessários
 * e redireciona para a página de login da Microsoft.
 */
export function startMicrosoftSso(intent: "login" | "signup" = "login"): void {
  if (typeof window === "undefined") return
  if (!isSsoAvailable()) return

  // `intent` só muda o texto que a tela de retorno mostra; não altera o que é permitido.
  // A API monta a autorização e troca o code: client id, tenant, redirect URI e secret nunca
  // dependem do build do front.
  window.location.href = `${API_BASE_URL}/api/auth/microsoft?intent=${intent}`
}

/** Provedores sociais habilitados no ambiente (flags do appsettings da API). */
export type SsoProviders = { microsoft: boolean; google: boolean; apple: boolean }

/** GET /api/auth/providers — quais botões de SSO exibir. */
export async function getSsoProviders(): Promise<SsoProviders> {
  return http.get<SsoProviders>("/api/auth/providers", { public: true, skipAuthRefresh: true, retries: 1 })
}

/**
 * POST /api/auth/logout — revoga no servidor a família de refresh tokens deste
 * dispositivo (as outras sessões do usuário seguem ativas) e apaga o estado local.
 *
 * As credenciais são capturadas antes e enviadas explicitamente, para que a sessão
 * local possa ser encerrada já no início: assim a navegação para o login que vem em
 * seguida não encontra resíduo algum que pudesse restaurar a sessão.
 *
 * Uma única tentativa, sem retry, e nenhum erro propagado — prender a interface em
 * backoff só para conseguir sair seria pior. Se a chamada não chegar, o refresh token
 * sobrevive até vencer: é o custo aceito de o usuário poder sair sempre.
 */
export async function logout(): Promise<void> {
  // O endpoint exige autenticação. Renovar aqui é o que garante que a revogação de
  // verdade aconteça, em vez de o servidor recusar um access token já vencido.
  const accessToken = await currentAccessToken()
  const refreshToken = await token.getRefreshToken()

  endSession()

  try {
    await http.post("/api/auth/logout", undefined, {
      // `public` apenas para o http.ts não tentar injetar nem renovar credenciais que
      // acabaram de ser descartadas; os headers abaixo já carregam o necessário.
      public: true,
      retries: 0,
      timeout: 10_000,
      headers: {
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(refreshToken ? { [REFRESH_TOKEN_HEADER]: refreshToken } : {}),
      },
    })
  } catch {
    // Sair nunca falha para o usuário.
  }
}
